import './index.css'
import cn from 'clsx'
import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'hooks/use-redux'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import { getSubscription } from '@disco/state-management/selectors/subscription'
import Row from 'components/common/row'
import BillingContactInformation from 'components/common/subscribed-user-billing/billing-contact-information/BillingContactInformation'
import Button from 'components/ui/button'
import CustomDropdown from 'components/ui/dropdowns/custom-dropdown'
import Label from 'components/ui/label'
import TextField from 'components/ui/text-field'
import useScript from '@disco/utils/use-script'
import USAStates from './USAStates'
const GOOGLE_API_KEY = 'AIzaSyB4tD5JqiCdlCfCMHQbMf_cb4y0ruJGrjg'
const USANameList = ['usa', 'us', 'u.s.a', 'u.s']

const getAddressByAddressFinder = autocomplete => {
  const result = {
    address: '',
    zip: '',
    country: '',
    city: '',
    state: '',
  }
  if (!autocomplete) return result
  const place = autocomplete.getPlace()
  if (!place || !place.address_components) return result
  const cityTypes = ['locality', 'postal_town', 'sublocality_level_1']

  for (let field of cityTypes) {
    const city = place.address_components.find(component => {
      return component.types[0] === field
    })

    if (city) {
      result.city = city.long_name
      break
    }
  }

  for (const component of place.address_components) {
    const componentType = component.types[0]

    switch (componentType) {
      case 'street_number': {
        result.address = component.long_name
        break
      }

      case 'route': {
        result.address = `${result.address} ${component.long_name}`
        break
      }

      case 'postal_code': {
        result.zip = component.long_name
        break
      }

      case 'administrative_area_level_1': {
        result.state = component.short_name
        break
      }

      case 'country':
        result.country = component.short_name
        break
    }
  }

  return result
}

const getUSACheck = country => USANameList.includes(country?.toLowerCase())

const BillingInformation = ({
  onChange,
  hideCompany, // hideCompany is deprecated
  onEdit,
}: {
  onChange?: (...args: Array<any>) => any
  onEdit?: (...args: Array<any>) => any
  hideCompany?: boolean
}): React.ReactElement<'div'> => {
  const subscription = useSelector(getSubscription)
  const isPhone = useSelector(isDeviceTypePhone)
  const [showEnterAddressManually, setShowEnterAddressManually] =
    useState(false)
  const [addressFinderAutocomplete, setAddressFinderAutocomplete] =
    useState(null)
  const [selectUSA, setSelectUSA] = useState(getUSACheck(subscription?.country))
  const [showBillingContactInformation, setShowBillingContactInformation] =
    useState(false)
  const [addressFinder, setAddressFinder] = useState({
    value: null,
    required: true,
  })
  const [address_1, setAddress_1] = useState({
    value: subscription?.address ?? undefined,
    required: true,
  })
  const [address_2, setAddress_2] = useState({
    value: subscription?.address_2 ?? undefined,
    error: null,
    required: false,
  })
  const [country, setCountry] = useState({
    value: subscription?.country ?? undefined,
    required: true,
  })
  const [city, setCity] = useState({
    value: subscription?.city ?? undefined,
    required: true,
  })
  const [zipCode, setZipCode] = useState({
    value: subscription?.zip ?? undefined,
    required: true,
  })
  const [USAState, setUSAState] = useState<{
    value: string | undefined
    error: string | null
    required: boolean
  }>({
    value: subscription?.state ?? undefined,
    error: null,
    required: getUSACheck(subscription?.country),
  })
  const [email, setEmail] = useState({
    value: subscription?.email ?? undefined,
    required: true,
  })
  const [phone, setPhone] = useState({
    value: subscription?.phone ?? undefined,
    required: false,
  })
  const [company, setCompany] = useState({
    value: subscription?.company ?? undefined,
    required: false,
  })
  const [VAT, setVAT] = useState({
    value: '',
    required: false,
  })
  const [error, setError] = useState<string | null>(null)
  const addressFinderRef = useRef(null)
  const countryFinderRef = useRef(null)
  const stateFinderRef = useRef(null)
  const [loaded] = useScript(
    `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places&v=weekly`,
  )

  const hideVAT = true // not storing VAT currently

  const confirm = () => {
    setShowBillingContactInformation(true)
  }

  const USACheck = country => {
    const newValue = getUSACheck(country)
    setSelectUSA(newValue)
    return newValue
  }

  useEffect(() => {
    if (window.google && addressFinderRef.current) {
      const newState = new window.google.maps.places.Autocomplete(
        addressFinderRef.current,
        {
          fields: ['address_components'],
          types: ['address'],
        },
      )
      newState.addListener('place_changed', () => {
        setAddressFinder({ ...addressFinder, value: newState.getPlace() })
        const data = getAddressByAddressFinder(newState)
        setAddress_1({ ...address_1, value: data.address })
        setCountry({ ...country, value: data.country })
        setCity({ ...city, value: data.city })
        setZipCode({ ...zipCode, value: data.zip })
        setUSAState({
          ...USAState,
          value: data.state,
          required: USACheck(data.country),
        })
      })
      setAddressFinderAutocomplete(newState)
    }

    if (window.google && countryFinderRef.current) {
      const newState = new window.google.maps.places.Autocomplete(
        countryFinderRef.current,
        {
          fields: ['address_components'],
          types: ['country'],
        },
      )
      newState.addListener('place_changed', () => {
        const data = getAddressByAddressFinder(newState)
        setCountry({ ...country, value: data.country })
        setUSAState({ ...USAState, required: USACheck(data.country) })
      })
    }

    if (window.google && stateFinderRef.current) {
      const newState = new window.google.maps.places.Autocomplete(
        stateFinderRef.current,
        {
          fields: ['address_components'],
          types: ['(regions)'],
        },
      )
      newState.addListener('place_changed', () => {
        const data = getAddressByAddressFinder(newState)
        setCountry({ ...country, value: data.country })
        setUSAState({
          ...USAState,
          value: data.state,
          required: USACheck(data.country),
        })
      })
    }
  }, [
    loaded,
    showEnterAddressManually,
    addressFinderRef.current,
    countryFinderRef.current,
    stateFinderRef.current,
    showBillingContactInformation,
  ])
  useEffect(() => {
    if (!onChange) return
    let form: Record<string, any> = {
      email: email.value,
      phone: phone.value,
      company: company.value,
      address_2: '',
    }
    if (!hideVAT) form.vat_number = VAT.value

    if (showEnterAddressManually) {
      form = {
        ...form,
        address: address_1.value,
        address_2: address_2.value,
        country: country.value,
        city: city.value,
        zip: zipCode.value,
        state: USAState.value,
      }
    } else {
      const addressData = getAddressByAddressFinder(addressFinderAutocomplete)
      form = { ...form, ...addressData }
    }
    if (!form.address_2) delete form.address_2 // null and "" are not accepted by API

    onChange({
      form,
      confirm,
    })
  }, [
    addressFinder,
    address_1,
    address_2,
    country,
    city,
    zipCode,
    USAState,
    email,
    phone,
    company,
    hideVAT,
    VAT.value,
    showEnterAddressManually,
  ])

  const onInvalid = event => {
    event.preventDefault()
  }

  const onManualAddressEntryInvalid = event => {
    if (!showEnterAddressManually) {
      setError(
        'Enter the full address with the specified country, city, zip code',
      )
    }

    onInvalid(event)
  }

  return (
    <div className="BillingInformation">
      {showBillingContactInformation && (
        <Row columnsSize={[12]}>
          <div>
            <BillingContactInformation
              address={address_1.value}
              address_2={address_2.value}
              country={country.value}
              city={city.value}
              zip={zipCode.value}
              state={USAState.value}
              email={email.value}
              vat_number={VAT.value}
              onEdit={() => {
                if (onEdit) {
                  onEdit()
                }

                setShowBillingContactInformation(false)
              }}
            />
          </div>
        </Row>
      )}
      {!showBillingContactInformation && (
        <Row
          columnsSize={!isPhone ? [8, 4] : [12, 12]}
          marginBottom={!isPhone ? undefined : 'small'}
        >
          <div>
            <TextField
              inputRef={addressFinderRef}
              label="Address finder"
              required={!showEnterAddressManually}
              placeholder="Search for your address"
              onChange={({ target }) => {
                setError(null)

                if (!target.value) {
                  setAddressFinder({ ...addressFinder, value: null })
                }
              }}
              onInvalid={onInvalid}
              patternErrorMessage="This field is required" // This allows us to display an error under this input
              // when it occurs elsewhere (onManualAddressEntryInvalid).
              invalid={Boolean(error)}
              error={error}
            />
          </div>
          <div>
            <div className="BillingInformation_LinkContainer">
              <Button
                onClick={() => {
                  setError(null)
                  setAddressFinder({ ...addressFinder, value: null })
                  setShowEnterAddressManually(true)
                }}
                appearance="inline-link"
                caption="Or enter address manually"
                disabled={showEnterAddressManually}
              />
            </div>
          </div>
        </Row>
      )}
      {!showBillingContactInformation && (
        <div
          className={cn({
            'BillingInformation--HiddenSection': !showEnterAddressManually,
          })}
        >
          <Row columnsSize={!isPhone ? [6, 6] : [12, 12]}>
            <div>
              <TextField
                label="Address 1"
                required
                value={address_1.value}
                onChange={({ target }) => {
                  setError(null)
                  setAddress_1({ ...address_1, value: target.value })
                }}
                patternErrorMessage="This field is required"
                onInvalid={onManualAddressEntryInvalid}
              />
            </div>
            <div>
              <TextField
                label="Address 2"
                value={address_2.value}
                onChange={({ target }) => {
                  setAddress_2({ ...address_2, value: target.value })
                }}
              />
            </div>
          </Row>

          <Row columnsSize={!isPhone ? [3, 3, 3, 3] : [6, 6, 6, 6]}>
            <div>
              <TextField
                inputRef={countryFinderRef}
                label="Country"
                required
                value={country.value}
                onChange={({ target }) => {
                  setError(null)
                  setSelectUSA(
                    USANameList.includes(target.value?.toLowerCase()),
                  )
                  setCountry({ ...country, value: target.value })
                }}
                patternErrorMessage="This field is required"
                onInvalid={onManualAddressEntryInvalid}
              />
            </div>
            <div>
              <TextField
                label="Zip code"
                required
                value={zipCode.value}
                onChange={({ target }) => {
                  setError(null)
                  setZipCode({ ...zipCode, value: target.value })
                }}
                pattern={selectUSA ? '^\\d{5}$' : undefined}
                patternErrorMessage={
                  zipCode.value && selectUSA
                    ? 'Invalid zip code'
                    : 'This field is required'
                }
                onInvalid={onManualAddressEntryInvalid}
              />
            </div>
            <div>
              <TextField
                label="City"
                required
                value={city.value}
                onChange={({ target }) => {
                  setError(null)
                  setCity({ ...city, value: target.value })
                }}
                patternErrorMessage="This field is required"
                onInvalid={onManualAddressEntryInvalid}
              />
            </div>
            <div>
              <Label caption="State" required={USAState.required} />
              {USAState.required ? (
                <>
                  <CustomDropdown
                    value={USAState.value}
                    options={Object.keys(USAStates).map(key => {
                      return {
                        value: USAStates[key],
                        label: USAStates[key],
                      }
                    })}
                    onChange={option => {
                      setError(null)
                      setUSAState({
                        ...USAState,
                        error: null,
                        value: option?.value || '',
                      })
                    }}
                  />
                  {/* TODO: add validation for ArtistSelection / CustomDropdown */}
                  <TextField
                    value={USAState.value}
                    required
                    patternErrorMessage="This field is required"
                    onInvalid={event => {
                      onManualAddressEntryInvalid(event)
                      setUSAState({
                        ...USAState,
                        error: 'This field is required',
                      })
                    }}
                    hidden
                  />
                </>
              ) : (
                <TextField
                  inputRef={stateFinderRef}
                  value={USAState.value}
                  onChange={({ target }) => {
                    setUSAState({ ...USAState, value: target.value })
                  }}
                />
              )}
            </div>
          </Row>
        </div>
      )}
      <Row
        columnsSize={
          hideCompany || hideVAT
            ? [4, 4, 0, 4]
            : !isPhone
            ? [3, 3, 3, 3]
            : [12, 12, 12, 12]
        }
      >
        <div>
          <TextField
            type="email"
            label="Email"
            required
            value={email.value}
            onChange={({ target }) => {
              setEmail({ ...email, value: target.value })
            }}
            patternErrorMessage="This field is required"
            onInvalid={onInvalid}
          />
        </div>
        <div>
          <TextField
            pattern="[\+|\(|\)\-| |0-9]+"
            name="phone"
            label="Phone"
            value={phone.value}
            onChange={({ target }) => {
              setPhone({ ...phone, value: target.value })
            }}
            onInvalid={onInvalid}
            patternErrorMessage="Invalid phone number"
          />
        </div>

        {hideCompany ? null : (
          <div>
            <TextField
              label="Company"
              onChange={({ target }) => {
                setCompany({ ...company, value: target.value })
              }}
            />
          </div>
        )}

        {hideVAT ? null : (
          <div>
            <TextField
              label="VAT"
              value={VAT.value}
              onChange={({ target }) => {
                setVAT({ ...VAT, value: target.value })
              }}
            />
          </div>
        )}
      </Row>
    </div>
  )
}

export default BillingInformation
